import { getExtension } from './publicS3Upload'

const DIRECT_API_URL =
  'https://pt8lbf5drh.execute-api.us-east-1.amazonaws.com/dev/walllabs_create'

const PROXY_API_URL = '/api/walllabs_create'

const X_API_KEY = (
  process.env.VUE_APP_WALLLABS_API_KEY ||
  'p4DCTzH6tm2ctNyS0VE344fenvBDJfCq7GDaj6lC'
).trim()

export const S3_WALLPAPER_BASE =
  'https://walllabs-bucket.s3.amazonaws.com/wallpaper/'

function getHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-Api-Key': X_API_KEY
  }
}

function getApiUrlCandidates() {
  const configured = String(process.env.VUE_APP_WALLLABS_API_URL || '').trim()
  const candidates = []
  const isDev = process.env.NODE_ENV === 'development'

  if (isDev) {
    if (typeof window !== 'undefined') {
      candidates.push(PROXY_API_URL)
    }
  } else {
    if (configured) {
      candidates.push(configured)
    }
    if (!candidates.includes(DIRECT_API_URL)) {
      candidates.push(DIRECT_API_URL)
    }
    if (typeof window !== 'undefined' && !candidates.includes(PROXY_API_URL)) {
      candidates.push(PROXY_API_URL)
    }
  }

  if (isDev) {
    if (configured && !candidates.includes(configured)) {
      candidates.push(configured)
    }
    if (!candidates.includes(DIRECT_API_URL)) {
      candidates.push(DIRECT_API_URL)
    }
  }

  return candidates
}

function isHtmlApiResponse(text) {
  const sample = String(text || '').trim().slice(0, 200).toLowerCase()
  return (
    sample.startsWith('<!doctype') ||
    sample.startsWith('<html') ||
    sample.includes('without javascript enabled')
  )
}

function isRetryableApiError(error) {
  const message = String(error?.message || '').toLowerCase()
  return (
    error?.name === 'TypeError' ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('load failed') ||
    message.includes('cors') ||
    message.includes('invalid api response') ||
    message.includes('spa fallback')
  )
}

async function requestOnce(apiUrl, body) {
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  })

  const text = await response.text()
  let data = {}

  if (isHtmlApiResponse(text)) {
    throw new Error(
      'Invalid API response: SPA fallback HTML (configure API URL or CloudFront /api/* behavior)'
    )
  }

  try {
    data = text ? JSON.parse(text) : {}
  } catch {
    throw new Error('Invalid API response: expected JSON')
  }

  if (!response.ok) {
    throw new Error(
      data?.Message || data?.message || `API Error: ${response.status}`
    )
  }

  if (data?.Status && String(data.Status).toUpperCase() !== 'SUCCESS') {
    throw new Error(data?.Message || data?.message || 'Request failed')
  }

  return data
}

export async function wallLabsRequest(body) {
  const candidates = getApiUrlCandidates()
  let lastError = null

  for (let index = 0; index < candidates.length; index += 1) {
    const apiUrl = candidates[index]
    try {
      return await requestOnce(apiUrl, body)
    } catch (error) {
      lastError = error
      const canRetry =
        index < candidates.length - 1 && isRetryableApiError(error)
      if (!canRetry) {
        throw error
      }
    }
  }

  throw lastError || new Error('API request failed')
}

export function listWallpapers() {
  return wallLabsRequest({ command: 'listWallpapers' })
}

export function addWallpaper({ wallpaper_name, category, s3_url }) {
  return wallLabsRequest({
    command: 'addWallpaper',
    wallpaper_name: String(wallpaper_name || '').trim(),
    category: String(category || '').trim(),
    s3_url: String(s3_url || '').trim()
  })
}

async function fileToBase64(file) {
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

/** Optional: backend may implement command uploadWallpaper on walllabs_create. */
export async function uploadWallpaperViaCatalogApi(file, s3Key) {
  const image_base64 = await fileToBase64(file)
  const data = await wallLabsRequest({
    command: 'uploadWallpaper',
    s3_key: String(s3Key || '').trim(),
    content_type: file.type || 'application/octet-stream',
    image_base64
  })

  const url =
    data?.s3_url || data?.url || data?.s3Url || data?.data?.s3_url || ''
  if (!url) {
    throw new Error(data?.Message || data?.message || 'Upload API returned no URL')
  }

  return url
}

export function buildDefaultS3Url(wallpaperName, fileName = '') {
  const title = String(wallpaperName || '').trim() || 'wallpaper'
  const extension = getExtension(fileName, '')
  const safeTitle = encodeURIComponent(title.replace(/[\\/:*?"<>|#%{}^~[\]`]/g, ''))
  return `${S3_WALLPAPER_BASE}${safeTitle}.${extension}`
}
